/**
 * Analytics tracking utilities for Google Analytics
 *
 * Tracks growth metrics:
 * - New sign-ups vs returning logins
 * - Weekly/Daily active users (automatic via GA)
 * - Feature engagement
 * - User retention patterns
 *
 * Cross-device tracking:
 * - Uses User ID to link sessions when users authenticate
 * - Same user on multiple devices = 1 user in reports
 */

// GA4 Measurement ID
const GA_MEASUREMENT_ID = 'G-KCSCJV4WVK';

// Check if gtag is available
function gtag(...args: unknown[]): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
}

// Storage key for tracking first-time users
const FIRST_AUTH_KEY = 'trackmind_first_auth';

/**
 * Set User ID for cross-device tracking.
 *
 * The raw identifier is NEVER sent. It would be a Google account id or a
 * GitHub username — the latter is straightforwardly personal information, and
 * Google Analytics' own terms forbid sending PII. We send a SHA-256 hash
 * instead: still stable, so one person across devices is still one user in
 * reports, but not reversible into an account.
 *
 * Hashing is async, so this is fire-and-forget; a failure just means no
 * cross-device linking for that session, which is not worth surfacing.
 */
export function setUserId(userId: string): void {
  hashIdentifier(userId)
    .then((hashed) => {
      gtag('config', GA_MEASUREMENT_ID, { user_id: hashed });
      console.log('[Analytics] Set hashed user_id for cross-device tracking');
    })
    .catch(() => {
      /* no cross-device linking this session */
    });
}

/** SHA-256, hex, truncated — deterministic and not reversible. */
async function hashIdentifier(value: string): Promise<string> {
  const data = new TextEncoder().encode(`trackmind:${value}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

/**
 * Check if this is a first-time user (new sign-up)
 */
function isFirstTimeUser(): boolean {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem(FIRST_AUTH_KEY) === null;
}

/**
 * Mark user as having signed up
 */
function markUserAsSignedUp(): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(FIRST_AUTH_KEY, new Date().toISOString());
  }
}

/**
 * Track user authentication (sign-up or login)
 * Automatically distinguishes between new and returning users
 */
export function trackAuth(method: 'google' | 'github'): void {
  const isNewUser = isFirstTimeUser();

  if (isNewUser) {
    // New user sign-up
    gtag('event', 'sign_up', {
      method: method,
    });
    markUserAsSignedUp();
    console.log('[Analytics] Tracked new sign-up via', method);
  } else {
    // Returning user login
    gtag('event', 'login', {
      method: method,
    });
    console.log('[Analytics] Tracked returning login via', method);
  }
}

/**
 * Track session start (when app loads with authenticated user)
 */
export function trackSessionStart(method: 'google' | 'github'): void {
  gtag('event', 'session_start', {
    auth_method: method,
  });
}

/**
 * Track feature usage - helps identify valuable features
 */
export function trackFeatureUse(
  feature:
    | 'task_create'
    | 'task_complete'
    | 'habit_complete'
    | 'project_create'
    | 'goal_create'
    | 'note_create'
    | 'journal_entry'
    | 'mood_log'
    | 'sync_manual'
    | 'list_create'
    | 'schedule_view'
    | 'export_project_csv'
    | 'sequence_link'
    | 'sequence_unlink'
    | 'sequence_capture'
    | 'review_complete'
    | 'fitness_start'
    | 'fitness_finish',
  metadata?: Record<string, string | number>
): void {
  gtag('event', feature, metadata);
}

/**
 * Track page/view navigation
 */
export function trackPageView(pageName: string): void {
  gtag('event', 'page_view', {
    page_title: pageName,
    page_location: window.location.href,
  });
}

/**
 * Track user engagement metrics
 */
export function trackEngagement(action: string, value?: number): void {
  gtag('event', 'engagement', {
    action: action,
    value: value,
  });
}

/**
 * Track errors for debugging
 */
export function trackError(errorType: string, errorMessage: string): void {
  gtag('event', 'exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal: false,
  });
}
